// ============================================================
// FONADHOO CRM v3.0 - CAMPAIGN CALENDAR
// Combined planning view over events + trips:
//   1) Month calendar (events + trips together)
//   2) Coverage gaps (islands by last-touched)
//   3) Impact / ROI (support gained after completed items)
// ============================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from './shared/Icon';

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const SUPPORTERS = new Set(['Strong Support', 'Leaning Support']);

const card = { background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', borderRadius: 14, padding: 20, marginBottom: 16 };

export default function CampaignCalendar({ supabase, allVoters = [], allLocations = [] }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [trips, setTrips] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [selectedDay, setSelectedDay] = useState(null);

  const voterById = useMemo(() => new Map((allVoters || []).map(v => [v.id, v])), [allVoters]);

  useEffect(() => {
    (async () => {
      const since = new Date(Date.now() - 180 * 86400000).toISOString();
      const [ev, tr, hist] = await Promise.all([
        supabase.from('events').select('id, name, date, island, type, completed, cost, actual_attendance'),
        supabase.from('campaign_trips').select('id, title, start_date, end_date, atoll, islands, status, cost'),
        supabase.from('support_history').select('voter_id, changed_from, changed_to, changed_at').gte('changed_at', since),
      ]);
      setEvents(ev.data || []);
      setTrips(tr.data || []);
      setHistory(hist.data || []);
      setLoading(false);
    })();
  }, [supabase]);

  // ── Map each calendar day → its items ───────────────────────
  const itemsByDay = useMemo(() => {
    const map = {};
    const push = (dstr, item) => { (map[dstr] = map[dstr] || []).push(item); };
    for (const e of events) {
      if (e.date) push(e.date, { kind: 'event', id: e.id, label: e.name, color: '#f59e0b' });
    }
    for (const t of trips) {
      if (!t.start_date) continue;
      const start = new Date(t.start_date);
      const end = t.end_date ? new Date(t.end_date) : start;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        push(toISO(d), { kind: 'trip', id: t.id, label: t.title, color: '#10b981' });
      }
    }
    return map;
  }, [events, trips]);

  // ── Month grid (6 weeks from the Sunday before the 1st) ─────
  const weeks = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const gridStart = new Date(first);
    gridStart.setDate(1 - first.getDay());
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart); d.setDate(gridStart.getDate() + i);
      cells.push(d);
    }
    return Array.from({ length: 6 }, (_, w) => cells.slice(w * 7, w * 7 + 7));
  }, [cursor]);

  // ── Coverage: islands by last-touched (events + trips) ──────
  const coverage = useMemo(() => {
    const islands = [...new Set([
      ...(allVoters || []).map(v => v.island).filter(Boolean),
      ...(allLocations || []).map(l => l.island).filter(Boolean),
    ])];
    const lastByIsland = {};
    const note = (isl, dstr) => { if (!isl || !dstr) return; if (!lastByIsland[isl] || dstr > lastByIsland[isl]) lastByIsland[isl] = dstr; };
    for (const e of events) note(e.island, e.date);
    for (const t of trips) { for (const isl of (t.islands || [])) note(isl, t.end_date || t.start_date); }
    const todayMs = Date.now();
    return islands.map(isl => {
      const last = lastByIsland[isl] || null;
      const days = last ? Math.floor((todayMs - new Date(last).getTime()) / 86400000) : null;
      return { island: isl, last, days };
    }).sort((a, b) => (b.days ?? Infinity) - (a.days ?? Infinity));
  }, [events, trips, allVoters, allLocations]);

  // ── Impact / ROI for completed items ────────────────────────
  const gainedFor = (islandList, dateStr, days = 30) => {
    if (!dateStr) return 0;
    const start = new Date(dateStr).getTime();
    const end = start + days * 86400000;
    const islSet = new Set((islandList || []).filter(Boolean));
    return history.filter(h => {
      const t = new Date(h.changed_at).getTime();
      if (t < start || t > end) return false;
      if (!(SUPPORTERS.has(h.changed_to) && !SUPPORTERS.has(h.changed_from))) return false;
      const v = voterById.get(h.voter_id);
      return v && islSet.has(v.island);
    }).length;
  };
  const impact = useMemo(() => {
    const rows = [];
    for (const e of events.filter(e => e.completed)) {
      rows.push({ kind: 'event', id: e.id, label: e.name, date: e.date, cost: Number(e.cost) || 0, gained: gainedFor([e.island], e.date) });
    }
    for (const t of trips.filter(t => t.status === 'completed')) {
      rows.push({ kind: 'trip', id: t.id, label: t.title, date: t.end_date || t.start_date, cost: Number(t.cost) || 0, gained: gainedFor(t.islands, t.end_date || t.start_date) });
    }
    return rows.sort((a, b) => b.gained - a.gained).slice(0, 12);
  }, [events, trips, history, voterById]); // eslint-disable-line

  const todayISO = toISO(new Date());

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-2)' }}>
        <div style={{ width: 40, height: 40, border: '3px solid var(--border-2)', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        Loading calendar…
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 18 }}>
        <span style={{ width: 38, height: 38, borderRadius: 11, display: 'grid', placeItems: 'center', background: '#3b82f61f', color: '#3b82f6' }}>
          <Icon name="calendar" size={20} />
        </span>
        <div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 2 }}>Planning</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 600, margin: 0, color: 'var(--text-strong)', letterSpacing: '-0.3px', lineHeight: 1 }}>Campaign Calendar</h2>
        </div>
      </div>

      {/* Month calendar */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: 'var(--text)' }}>{MONTHS[cursor.m]} {cursor.y}</h3>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => setCursor(c => ({ y: c.m === 0 ? c.y - 1 : c.y, m: c.m === 0 ? 11 : c.m - 1 }))} style={navBtn}>‹</button>
              <button onClick={() => { const d = new Date(); setCursor({ y: d.getFullYear(), m: d.getMonth() }); }} style={{ ...navBtn, width: 'auto', padding: '0 10px', fontSize: 11, fontFamily: "'DM Mono', monospace" }}>Today</button>
              <button onClick={() => setCursor(c => ({ y: c.m === 11 ? c.y + 1 : c.y, m: c.m === 11 ? 0 : c.m + 1 }))} style={navBtn}>›</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 14, fontSize: 11, fontFamily: "'DM Mono', monospace", color: 'var(--text-3)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: 2, background: '#f59e0b' }} /> Event</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: 2, background: '#10b981' }} /> Trip</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {WEEKDAYS.map(w => (
            <div key={w} style={{ textAlign: 'center', fontSize: 10, fontFamily: "'DM Mono', monospace", color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: 1, paddingBottom: 4 }}>{w}</div>
          ))}
          {weeks.flat().map((d, i) => {
            const dstr = toISO(d);
            const inMonth = d.getMonth() === cursor.m;
            const items = itemsByDay[dstr] || [];
            const isToday = dstr === todayISO;
            const isSel = dstr === selectedDay;
            return (
              <div key={i} onClick={() => setSelectedDay(isSel ? null : dstr)}
                style={{
                  minHeight: 76, borderRadius: 8, padding: 5, cursor: 'pointer',
                  background: isSel ? 'rgba(59,130,246,0.13)' : 'var(--surface-2)',
                  border: isToday ? '1px solid #3b82f6' : isSel ? '1px solid rgba(59,130,246,0.5)' : '1px solid var(--border)',
                  opacity: inMonth ? 1 : 0.4, overflow: 'hidden',
                }}>
                <div style={{ fontSize: 11, fontWeight: isToday ? 700 : 500, color: isToday ? '#60a5fa' : 'var(--text-2)', fontVariantNumeric: 'tabular-nums', marginBottom: 3 }}>{d.getDate()}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {items.slice(0, 3).map((it, k) => (
                    <div key={k} title={it.label}
                      style={{ fontSize: 9.5, lineHeight: 1.3, padding: '1px 4px', borderRadius: 4, background: it.color + '22', color: it.color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {it.label}
                    </div>
                  ))}
                  {items.length > 3 && <div style={{ fontSize: 9, color: 'var(--text-4)' }}>+{items.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Selected day detail */}
        {selectedDay && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--text-3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>{selectedDay}</div>
            {(itemsByDay[selectedDay] || []).length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-4)' }}>Nothing scheduled.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(itemsByDay[selectedDay] || []).map((it, k) => (
                  <div key={k} onClick={() => navigate(it.kind === 'event' ? '/events' : '/trips')}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer' }}>
                    <Icon name={it.kind === 'event' ? 'calendar' : 'plane'} size={15} color={it.color} />
                    <span style={{ fontSize: 13, color: 'var(--text)', flex: 1 }}>{it.label}</span>
                    <span style={{ fontSize: 10, fontFamily: "'DM Mono', monospace", color: 'var(--text-4)', textTransform: 'uppercase' }}>{it.kind}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
        {/* Coverage gaps */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
            <span style={{ width: 30, height: 30, borderRadius: 8, display: 'grid', placeItems: 'center', background: '#ef44441f', color: '#ef4444' }}><Icon name="mapPin" size={16} /></span>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: 'var(--text)' }}>Coverage Gaps</h3>
          </div>
          {coverage.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-4)' }}>No islands yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {coverage.map(c => {
                const stale = c.days === null || c.days >= 30;
                const col = c.days === null ? '#ef4444' : c.days >= 30 ? '#f59e0b' : '#10b981';
                return (
                  <div key={c.island} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: col, flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 13, color: 'var(--text)' }}>{c.island}</span>
                    <span style={{ fontSize: 11.5, fontFamily: "'DM Mono', monospace", color: stale ? col : 'var(--text-3)' }}>
                      {c.days === null ? 'never visited' : c.days === 0 ? 'today' : `${c.days}d ago`}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Impact / ROI */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
            <span style={{ width: 30, height: 30, borderRadius: 8, display: 'grid', placeItems: 'center', background: '#10b9811f', color: '#10b981' }}><Icon name="trending" size={16} /></span>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: 'var(--text)' }}>Impact</h3>
            <span style={{ fontSize: 10, color: 'var(--text-4)', fontFamily: "'DM Mono', monospace" }}>support gained ≤30d after</span>
          </div>
          {impact.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-4)' }}>No completed events or trips yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {impact.map(r => (
                <div key={r.kind + r.id} onClick={() => navigate(r.kind === 'event' ? '/events' : '/trips')}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer' }}>
                  <Icon name={r.kind === 'event' ? 'calendar' : 'plane'} size={15} color="var(--text-4)" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</div>
                    {r.cost > 0 && <div style={{ fontSize: 10.5, color: 'var(--text-4)', fontFamily: "'DM Mono', monospace" }}>MVR {r.cost.toLocaleString()}{r.gained > 0 ? ` · ${Math.round(r.cost / r.gained).toLocaleString()}/supporter` : ''}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: r.gained > 0 ? '#10b981' : 'var(--text-4)', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>+{r.gained}</div>
                    <div style={{ fontSize: 9, color: 'var(--text-4)', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase' }}>gained</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const navBtn = { width: 28, height: 28, borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-2)', cursor: 'pointer', fontSize: 16, lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
